import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ICellEditor, ICellEditorParams } from 'ag-grid-community';
import dayjs from 'dayjs';
import { DatePickerChange } from '@tag/tag-components-react-v4';

import DatePickerTag4 from '@/components/common/DatePicker/DatePickerTag4';
import { getDateWithProperTimeOffset } from '@/utils/date.utils';

export interface AgGridDatepickerTag4EditorReference extends ICellEditor {
  applyChanges: (selectedDate: Date | null) => void;
  cancelChanges: () => void;
  getChosenValue: () => Date | null;
}

export type HandleChangeDate = (
  selectedDate: Date | null,
  applyChanges: (selectedDate: Date | null) => void,
  cancelChanges: () => void
) => void;

export interface IAgGridDatepickerTag4EditorProps extends ICellEditorParams {
  saveOnChange?: boolean;
  handleChangeDate?: HandleChangeDate;
  keepFocusOnCalendarClosed?: boolean;
  pickerSize?: 'small' | 'default';
  clearButton?: boolean;
  placeholder?: string;
}

const AgGridDatepickerTag4Editor = (
  props: IAgGridDatepickerTag4EditorProps,
  ref: ForwardedRef<AgGridDatepickerTag4EditorReference>
) => {
  const {
    saveOnChange = false,
    value,
    api,
    handleChangeDate,
    keepFocusOnCalendarClosed = false,
    clearButton = false,
    pickerSize = 'default',
    placeholder = 'dd/mm/yyyy' // Default fallback
  } = props;

  const datePickerRef = useRef<HTMLDivElement>(null);
  const [currentDate, setCurrentDate] = useState<Date | null>(value ?? null);
  const [isEditing, setIsEditing] = useState(true);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const chosenValueRef = useRef<Date | null>(value ?? null);

  // Tracks the latest value synchronously via a ref, updated immediately
  // in onChange before any async React state update. This ensures getValue()
  // always returns the most recent value even when AG Grid calls it
  // synchronously during stopEditing() (e.g. on Tab navigation), before
  // React has re-rendered with the new state.
  const latestValueRef = useRef<Date | null>(value ?? null);

  const valueToShowInsideDatepicker =
    currentDate && dayjs(currentDate).isValid() ? new Date(currentDate) : null;

  const applyChanges = (selectedDate: Date | null) => {
    setCurrentDate(selectedDate);
    chosenValueRef.current = selectedDate;
    latestValueRef.current = selectedDate;

    if (saveOnChange) {
      setIsEditing(false);
    }
  };

  const cancelChanges = () => {
    setIsEditing(false);
  };

  const setInputFocus = () => {
    if (datePickerRef.current) {
      const input = datePickerRef.current.querySelector('input');
      input?.focus();
    }
  };

  const onChange = (event: DatePickerChange) => {
    const selectedDate = event.value;

    // Update the ref synchronously so getValue() always has the latest value,
    // even if AG Grid calls it before React processes the state update.
    latestValueRef.current = selectedDate ?? null;
    
    if (typeof handleChangeDate === 'function') {
      if (!selectedDate) {
        handleChangeDate(null, applyChanges, cancelChanges);
        return;
      }

      const normalizedDate = dayjs(selectedDate).startOf('day').toDate();
      const date = getDateWithProperTimeOffset(normalizedDate);
      chosenValueRef.current = date;
      latestValueRef.current = date;

      if (keepFocusOnCalendarClosed) {
        setTimeout(() => setInputFocus(), 0);
      }

      // Tag4 DatePicker already validates, so we check if we have a valid date
      if (selectedDate) {
        handleChangeDate(date, applyChanges, cancelChanges);
      }
    } else {
      if (!selectedDate) {
        applyChanges(null);
        return;
      }

      const normalizedDate = dayjs(selectedDate).startOf('day').toDate();
      const date = getDateWithProperTimeOffset(normalizedDate);
      chosenValueRef.current = date;
      latestValueRef.current = date;

      if (keepFocusOnCalendarClosed) {
        setTimeout(() => setInputFocus(), 0);
      }

      if (selectedDate) {
        applyChanges(date);
      }
    }
  };

  const setCalendarOpen = (open: boolean) => {
    if (!datePickerRef.current) return;

    if (open) {
      const input = datePickerRef.current.querySelector('input');
      input?.focus();
      // Trigger calendar open by simulating click on calendar icon
      const calendarButton = 
      datePickerRef.current.querySelector('button[aria-label*="calendar"], button[type="button"]');
      if (calendarButton && !isCalendarOpen) {
        (calendarButton as HTMLButtonElement).click();
        setIsCalendarOpen(true);
      }
    } else {
      // Close by clicking outside or pressing escape
      const input = datePickerRef.current.querySelector('input');
      input?.blur();
      setIsCalendarOpen(false);
    }
  };

  useEffect(() => {
    if (!isEditing && saveOnChange) {
      api.stopEditing();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isEditing]);

  // Auto-focus on mount (AG Grid requirement for cell editors)
  useEffect(() => {
    setTimeout(() => setInputFocus(), 0);
  }, []);

  /**
   * Expose AG Grid required methods via ref
   */
  useImperativeHandle(ref, () => ({
    /**
     * AG Grid calls getValue() synchronously during stopEditing().
     * When Tab is pressed, AG Grid first focuses the cell wrapper (causing
     * the DatePicker input to blur and fire onChange), then immediately calls
     * stopEditing() → getValue(). Because onChange updates React state
     * asynchronously (via setCurrentDate), the state may not reflect the
     * latest input yet. We use latestValueRef which is updated synchronously
     * in onChange, so it always has the correct value.
     */
    getValue() {
      return latestValueRef.current;
    },

    focusIn() {
      setInputFocus();
    },

    focusOut() {
      setCalendarOpen(false);
    },

    applyChanges,
    cancelChanges,
    getChosenValue: () => chosenValueRef.current
  }));

  return (
    <div className="ag-grid-datepicker" ref={datePickerRef}>
      <DatePickerTag4
        value={valueToShowInsideDatepicker}
        onChange={onChange}
        pickerSize={pickerSize}
        clearButton={clearButton}
        placeholder={placeholder}
      />
    </div>
  );
};

export default forwardRef(AgGridDatepickerTag4Editor);
